import validate from '@vlabs/shared/validators';

import { i18nChartFormTranslation } from '../../i18n';

const filterFunctionSets = {
  likeNlike: ['like', 'nlike'],
  inNin: ['in', 'nin'],
  eqNeq: ['eq', 'neq'],
  gtGteLtLte: ['gt', 'gte', 'lt', 'lte'],
  covered: ['covered'],
};

const commonFunctions = {
  inNinEqNeq: [...filterFunctionSets.inNin, ...filterFunctionSets.eqNeq],
  inNinEqNeqGtGteLtLte: [
    ...filterFunctionSets.inNin,
    ...filterFunctionSets.eqNeq,
    ...filterFunctionSets.gtGteLtLte,
  ],
};

export const FILTER_FIELDS = {
  tags: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.tags');
    },
    serverField: 'tags',
    availableFunctions: filterFunctionSets.likeNlike,
    format: 'input',
  },

  source: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.source');
    },
    serverField: 'source',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'source',
  },

  stream_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.stream_id');
    },
    serverField: 'stream_id',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
    validateRule: validate.uuid,
  },

  user_data: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.user_data');
    },
    serverField: 'user_data',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
  },

  event_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.event_id');
    },
    serverField: 'event_id',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
    validateRule: validate.uuid,
  },

  external_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.external_id');
    },
    serverField: 'external_id',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
  },

  handler_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.handler_id');
    },
    serverField: 'handler_id',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
    validateRule: validate.uuid,
  },

  top_similar_object_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.top_similar_object_id');
    },
    serverField: 'top_similar_object_id',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
    validateRule: validate.uuid,
  },

  top_similar_external_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.top_similar_external_id');
    },
    serverField: 'top_similar_external_id',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
  },

  top_matching_candidates_label: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.top_matching_candidates_label');
    },
    serverField: 'top_matching_candidates_label',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
    conflictsWith: ['lists'],
  },

  lists: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.lists');
    },
    serverField: 'top_matching_candidates_label',
    availableFunctions: filterFunctionSets.inNin,
    format: 'select',
    optionsKey: 'lists',
    isMulti: true,
    conflictsWith: ['top_matching_candidates_label'],
  },

  top_similar_object_similarity: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.top_similar_object_similarity');
    },
    serverField: 'top_similar_object_similarity',
    availableFunctions: filterFunctionSets.gtGteLtLte,
    format: 'input',
    validateRule: validate.number,
  },

  face_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.face_id');
    },
    serverField: 'face_id',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
    validateRule: validate.uuid,
  },

  gender: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.gender');
    },
    serverField: 'gender',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'gender',
  },

  age: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.age');
    },
    serverField: 'age',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
    validateRule: validate.integer,
  },

  emotion: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.emotion');
    },
    serverField: 'emotion',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'emotion',
  },

  mask: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.mask');
    },
    serverField: 'mask',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'mask',
  },

  liveness: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.liveness');
    },
    serverField: 'liveness',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'liveness',
  },

  deepfake: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.deepfake');
    },
    serverField: 'deepfake',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'deepfake',
  },

  city: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.city');
    },
    serverField: 'city',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
  },

  area: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.area');
    },
    serverField: 'area',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
  },

  district: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.district');
    },
    serverField: 'district',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
  },

  street: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.street');
    },
    serverField: 'street',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
  },

  house_number: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.house_number');
    },
    serverField: 'house_number',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
  },

  geo_position: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.geo_position');
    },
    serverField: 'geo_position',
    availableFunctions: filterFunctionSets.covered,
    format: 'geolocation',
  },

  track_id: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.track_id');
    },
    serverField: 'track_id',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'input',
  },

  apparent_age: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.apparent_age');
    },
    serverField: 'apparent_age',
    availableFunctions: commonFunctions.inNinEqNeqGtGteLtLte,
    format: 'input',
    validateRule: validate.integer,
  },

  apparent_gender: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.apparent_gender');
    },
    serverField: 'apparent_gender',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'apparent_gender',
  },

  sleeve_length: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.sleeve_length');
    },
    serverField: 'sleeve_length',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'sleeve_length',
  },

  headwear_state: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.headwear_state');
    },
    serverField: 'headwear_state',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'headwear_state',
  },

  headwear_apparent_color: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.headwear_apparent_color');
    },
    serverField: 'headwear_apparent_color',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'headwear_apparent_color',
  },

  upper_clothing_colors: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.upper_clothing_colors');
    },
    serverField: 'upper_clothing_colors',
    availableFunctions: filterFunctionSets.inNin,
    format: 'select',
    optionsKey: 'upper_clothing_colors',
  },

  lower_garment_colors: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.lower_garment_colors');
    },
    serverField: 'lower_garment_colors',
    availableFunctions: filterFunctionSets.inNin,
    format: 'select',
    optionsKey: 'lower_garment_colors',
  },

  lower_garment_type: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.lower_garment_type');
    },
    serverField: 'lower_garment_type',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'lower_garment_type',
  },

  shoes_apparent_color: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.shoes_apparent_color');
    },
    serverField: 'shoes_apparent_color',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'shoes_apparent_color',
  },

  backpack_state: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.backpack_state');
    },
    serverField: 'backpack_state',
    availableFunctions: commonFunctions.inNinEqNeq,
    format: 'select',
    optionsKey: 'backpack_state',
  },

  meta: {
    get label(): string {
      return i18nChartFormTranslation('options.filter_field.meta');
    },
    serverField: 'meta',
    availableFunctions: [
      ...filterFunctionSets.likeNlike,
      ...commonFunctions.inNinEqNeqGtGteLtLte,
    ],
    format: 'input',
  },
};
